import React, { useState, useEffect } from 'react';
import { FileText, Calendar, DollarSign, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import billingCalculationService from '../services/billingCalculationService';
import stripeBillingService from '../services/stripeBillingService';

/**
 * Composant de génération manuelle de factures pour les clients
 * Permet de créer des factures basées sur les heures de rendez-vous
 */
const ClientInvoiceGenerator = ({ client, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [periodeDebut, setPeriodeDebut] = useState('');
  const [periodeFin, setPeriodeFin] = useState('');
  const [calculHeures, setCalculHeures] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fonction helper pour formater une date en YYYY-MM-DD sans problème de fuseau horaire
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    // Pré-remplir avec le mois précédent par défaut
    const maintenant = new Date();
    const annee = maintenant.getFullYear();
    const moisActuel = maintenant.getMonth();

    // Premier jour du mois précédent
    const premierJourMoisPrecedent = new Date(annee, moisActuel - 1, 1);

    // Dernier jour du mois précédent = jour 0 du mois actuel
    const dernierJourMoisPrecedent = new Date(annee, moisActuel, 0);

    console.log('📅 Période par défaut:', {
      debut: premierJourMoisPrecedent.toLocaleDateString('fr-FR'),
      fin: dernierJourMoisPrecedent.toLocaleDateString('fr-FR'),
      jourDuMois: dernierJourMoisPrecedent.getDate(),
      debutFormatted: formatDateForInput(premierJourMoisPrecedent),
      finFormatted: formatDateForInput(dernierJourMoisPrecedent)
    });

    setPeriodeDebut(formatDateForInput(premierJourMoisPrecedent));
    setPeriodeFin(formatDateForInput(dernierJourMoisPrecedent));
  }, []);

  /**
   * Calcule les heures facturables pour la période sélectionnée
   */
  const handleCalculerHeures = async () => {
    if (!periodeDebut || !periodeFin) {
      setError('Veuillez sélectionner une période valide');
      return;
    }

    try {
      setCalculationLoading(true);
      setError(null);
      setCalculHeures(null);

      const debut = new Date(periodeDebut);
      const fin = new Date(periodeFin);

      // Valider la période
      if (debut >= fin) {
        throw new Error('La date de début doit être avant la date de fin');
      }

      // Calculer les heures facturables (statuts: assigned et completed uniquement)
      const calcul = await billingCalculationService.calculerHeuresFacturables(
        client.id,
        debut,
        fin,
        ['assigne', 'completed'] // Uniquement les RDV assignés ou terminés
      );

      console.log('📊 Calcul des heures:', calcul);

      if (calcul.totalHeures === 0) {
        setError(`Aucune heure facturable trouvée pour la période du ${debut.toLocaleDateString('fr-FR')} au ${fin.toLocaleDateString('fr-FR')}`);
        setCalculHeures(null);
      } else {
        setCalculHeures(calcul);
      }

    } catch (err) {
      console.error('❌ Erreur calcul heures:', err);
      setError(err.message || 'Erreur lors du calcul des heures facturables');
      setCalculHeures(null);
    } finally {
      setCalculationLoading(false);
    }
  };

  /**
   * Génère la facture Stripe et l'attribue au client
   */
  const handleGenererFacture = async () => {
    if (!calculHeures) {
      setError('Veuillez d\'abord calculer les heures facturables');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Récupérer l'utilisateur connecté
      const auth = getAuth();
      const currentUser = auth.currentUser;
      let createdByName = 'Système';

      if (currentUser) {
        try {
          // Essayer de récupérer le nom depuis la collection employees
          const employeeDoc = await getDoc(doc(db, 'employees', currentUser.uid));
          if (employeeDoc.exists()) {
            const employeeData = employeeDoc.data();
            createdByName = `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() ||
                           employeeData.displayName ||
                           employeeData.email ||
                           'Utilisateur';
          } else {
            // Si pas dans employees, utiliser displayName ou email de Firebase Auth
            createdByName = currentUser.displayName || currentUser.email || 'Utilisateur';
          }
        } catch (error) {
          console.warn('Impossible de récupérer le nom de l\'utilisateur:', error);
          createdByName = currentUser.displayName || currentUser.email || 'Utilisateur';
        }
      }

      const periode = {
        debut: new Date(periodeDebut),
        fin: new Date(periodeFin),
        libelle: calculHeures.periode.libelle
      };

      // Générer la facture via le service Stripe
      const resultat = await stripeBillingService.genererFactureMandat(
        client.id,
        periode,
        calculHeures,
        {
          createdBy: createdByName,
          createdById: currentUser?.uid || 'system',
          source: 'manual_generation',
          metadata: {
            generatedFrom: 'ResourceManagement',
            clientName: `${client.firstName} ${client.lastName}`
          }
        }
      );

      console.log('✅ Facture générée avec succès:', resultat);

      setSuccess({
        message: `Facture générée avec succès !`,
        details: {
          produit: resultat.produit.name,
          montant: `${calculHeures.montantTotal.toFixed(2)} €`,
          heures: `${calculHeures.totalHeures}h sur ${calculHeures.nombreRendezVous} rendez-vous`,
          factureId: resultat.facture.id
        }
      });

      // Appeler le callback de succès après 2 secondes
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(resultat);
        }
      }, 2000);

    } catch (err) {
      console.error('❌ Erreur génération facture:', err);
      setError(err.message || 'Erreur lors de la génération de la facture');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Utilise une période pré-définie
   */
  const utiliserPeriodePredefinie = (type) => {
    const maintenant = new Date();
    const annee = maintenant.getFullYear();
    const mois = maintenant.getMonth();
    let debut, fin;

    switch (type) {
      case 'mois-courant':
        // Premier jour du mois actuel
        debut = new Date(annee, mois, 1);
        // Dernier jour du mois actuel = jour 0 du mois suivant
        fin = new Date(annee, mois + 1, 0);
        console.log('📅 Mois courant sélectionné:', {
          type,
          moisActuel: mois,
          debut: debut.toLocaleDateString('fr-FR'),
          fin: fin.toLocaleDateString('fr-FR'),
          nombreJours: fin.getDate(),
          debutFormatted: formatDateForInput(debut),
          finFormatted: formatDateForInput(fin)
        });
        break;
      case 'mois-precedent':
        // Premier jour du mois précédent
        debut = new Date(annee, mois - 1, 1);
        // Dernier jour du mois précédent = jour 0 du mois actuel
        fin = new Date(annee, mois, 0);
        console.log('📅 Mois précédent sélectionné:', {
          type,
          moisPrecedent: mois - 1,
          debut: debut.toLocaleDateString('fr-FR'),
          fin: fin.toLocaleDateString('fr-FR'),
          nombreJours: fin.getDate(),
          debutFormatted: formatDateForInput(debut),
          finFormatted: formatDateForInput(fin)
        });
        break;
      case 'trimestre-courant':
        const trimestreDebut = Math.floor(mois / 3) * 3;
        debut = new Date(annee, trimestreDebut, 1);
        fin = new Date(annee, trimestreDebut + 3, 0);
        console.log('📅 Trimestre courant sélectionné:', {
          type,
          trimestre: Math.floor(mois / 3) + 1,
          debut: debut.toLocaleDateString('fr-FR'),
          fin: fin.toLocaleDateString('fr-FR'),
          debutFormatted: formatDateForInput(debut),
          finFormatted: formatDateForInput(fin)
        });
        break;
      default:
        return;
    }

    setPeriodeDebut(formatDateForInput(debut));
    setPeriodeFin(formatDateForInput(fin));
    setCalculHeures(null); // Réinitialiser le calcul
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center mb-2">
          <FileText className="w-8 h-8 mr-3" />
          <div>
            <h3 className="text-xl font-bold">Générer une facture</h3>
            <p className="text-blue-100">
              Pour {client.firstName} {client.lastName}
            </p>
          </div>
        </div>
      </div>

      {/* Sélection de période */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-500" />
          Période de facturation
        </h4>

        {/* Périodes pré-définies */}
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            onClick={() => utiliserPeriodePredefinie('mois-precedent')}
            className="px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
          >
            Mois précédent
          </button>
          <button
            onClick={() => utiliserPeriodePredefinie('mois-courant')}
            className="px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
          >
            Mois en cours
          </button>
          <button
            onClick={() => utiliserPeriodePredefinie('trimestre-courant')}
            className="px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
          >
            Trimestre en cours
          </button>
        </div>

        {/* Sélection manuelle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={periodeDebut}
              onChange={(e) => {
                setPeriodeDebut(e.target.value);
                setCalculHeures(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date de fin
            </label>
            <input
              type="date"
              value={periodeFin}
              onChange={(e) => {
                setPeriodeFin(e.target.value);
                setCalculHeures(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Bouton calculer */}
        <button
          onClick={handleCalculerHeures}
          disabled={calculationLoading || !periodeDebut || !periodeFin}
          className="mt-4 w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {calculationLoading ? (
            <>
              <Loader className="w-5 h-5 mr-2 animate-spin" />
              Calcul en cours...
            </>
          ) : (
            <>
              <DollarSign className="w-5 h-5 mr-2" />
              Calculer les heures facturables
            </>
          )}
        </button>
      </div>

      {/* Résultat du calcul */}
      {calculHeures && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
            Résultat du calcul
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total d'heures</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {calculHeures.totalHeures.toFixed(2)}h
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Nombre de rendez-vous</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {calculHeures.nombreRendezVous}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Montant total</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {calculHeures.montantTotal.toFixed(2)} €
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Détails</p>
            <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
              <p>• Frais de mandat unitaire : {calculHeures.fraisMandatUnitaire} €/h</p>
              <p>• Période : {calculHeures.periode.libelle}</p>
              <p>
                • Rendez-vous inclus : {calculHeures.rendezVousDetails.length} intervention(s)
              </p>
            </div>
          </div>

          {/* Bouton générer facture */}
          <button
            onClick={handleGenererFacture}
            disabled={loading}
            className="mt-4 w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Génération en cours...
              </>
            ) : (
              <>
                <FileText className="w-5 h-5 mr-2" />
                Générer la facture Stripe
              </>
            )}
          </button>
        </div>
      )}

      {/* Messages d'erreur */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">Erreur</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Message de succès */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {success.message}
              </p>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300 space-y-1">
                <p>• Produit : {success.details.produit}</p>
                <p>• Montant : {success.details.montant}</p>
                <p>• Heures : {success.details.heures}</p>
                <p className="text-xs opacity-75">• ID : {success.details.factureId}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          {success ? 'Fermer' : 'Annuler'}
        </button>
      </div>
    </div>
  );
};

export default ClientInvoiceGenerator;
